// Portal login `?returnTo=` (Q2 / A17). A logged-out visitor who opens an item
// link is sent to sign-in and brought back afterwards. The value arrives in the
// URL, so anyone can craft it: an unchecked redirect would turn the portal's
// sign-in page into an open redirect to a phishing site that looks like the
// next step of signing in.
//
// The rule is an allow-list, not a deny-list: the value must be a same-origin
// path under "/portal/", and every percent-decoding of it must be too. Anything
// else — an absolute URL of any scheme, a protocol-relative "//host" or
// "/\host", a backslash anywhere (browsers read "\" as "/"), control characters
// or whitespace (browsers strip some of them, changing what the URL means),
// dot segments that climb out of the prefix, or an encoding of any of these —
// yields null and the caller falls back to the dashboard.

const PORTAL_PREFIX = "/portal/";
const MAX_LENGTH = 2048;
// Decoding is repeated until the value stops changing; a value that is still
// changing after this many passes is rejected rather than trusted.
const MAX_DECODE_PASSES = 5;
// A throwaway origin to resolve against: whatever the value resolves to must
// keep exactly this origin, i.e. it named no host or scheme of its own.
const PROBE_ORIGIN = "https://portal-return.invalid";

// C0 controls, DEL, C1 controls, any (Unicode) whitespace, and backslash.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_CHARS = /[\u0000-\u001F\u007F-\u009F\s\\]/;

function isSafeLayer(value: string): boolean {
  if (!value.startsWith(PORTAL_PREFIX)) return false;
  if (FORBIDDEN_CHARS.test(value)) return false;
  const path = value.split(/[?#]/, 1)[0];
  if (path.split("/").some((segment) => segment === "." || segment === "..")) return false;
  let resolved: URL;
  try {
    resolved = new URL(value, PROBE_ORIGIN);
  } catch {
    return false;
  }
  return resolved.origin === PROBE_ORIGIN && resolved.pathname.startsWith(PORTAL_PREFIX);
}

/**
 * The validated return path, or null when `raw` is absent or unsafe. Callers
 * fall back to the viewer's dashboard (`dashboardPathFor`) on null.
 */
export function safePortalReturnTo(raw: string | null | undefined): string | null {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > MAX_LENGTH) return null;
  let current = raw;
  for (let pass = 0; ; pass += 1) {
    if (!isSafeLayer(current)) return null;
    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return null; // malformed escape (a stray "%")
    }
    if (decoded === current) return current;
    if (pass >= MAX_DECODE_PASSES) return null;
    current = decoded;
  }
}

/** Sign-in route that brings the visitor back to `returnTo` afterwards. */
export function portalLoginPathFor(returnTo: string): string {
  const safe = safePortalReturnTo(returnTo);
  return safe ? `/portal/login?returnTo=${encodeURIComponent(safe)}` : "/portal/login";
}
